import {
  onSnapshot,
  query,
  collection,
  doc,
  updateDoc,
  setDoc,
  writeBatch
} from 'firebase/firestore'
import { db, dbOmit, generateId } from 'src/controllers/db'
import { ICourse, IDay, IEntityType, IFree, ILesson, IWeek } from 'shared/types'
import { receiveCourses } from 'src/model/actions'
import store from 'src/model/store'
import { addListener } from 'controllers/listeners'
import dayjs from 'dayjs'
import forEach from 'lodash/forEach'
import map from 'lodash/map'

export const dbFetchCourses = async () => {
  try {
    const q = query(collection(db, 'courses'))
    const unsubscribe = onSnapshot(
      q,
      (sn) => {
        const res: Record<string, ICourse> = {}
        sn.forEach((doc) => {
          const p = doc.data() as ICourse
          res[doc.id] = {
            ...p,
            id: doc.id,
            createdAt: +dayjs(p.createdAt || 0)
          }
        })
        store.dispatch(receiveCourses(res))
      },
      (err) => {
        console.log(`dbFetchCourses error: ${err.message}`)
      }
    )
    addListener('courses', unsubscribe)
  } catch (e) {
    console.error('dbFetchCourses error', e)
  }
}

export const dbUpdateCourse = async (
  courseId: string,
  upd: Partial<ICourse>
) => {
  try {
    console.log('dbUpdateCourse', courseId, upd)
    const ref = doc(collection(db, 'courses'), courseId)
    await updateDoc(ref, dbOmit(upd))
  } catch (e) {
    console.log('dbUpdateCourse error', e)
  }
}

export const dbCreateCourse = async () => {
  const id = generateId()
  const c: ICourse = {
    id,
    title: 'Новый курс',
    invisible: true,
    closed: true,
    price: 0,
    lessons: [],
    sections: {},
    sectionsOrder: [],
    createdAt: +dayjs()
  }
  const ref = doc(collection(db, 'courses'), id)
  await setDoc(ref, dbOmit(c))
  return id
}

export const dbDeleteCourse = async (c: ICourse) => {
  const batch = writeBatch(db)
  batch.delete(doc(collection(db, 'courses'), c.id))
  if (c.lessons) {
    for (const lId of c.lessons) {
      batch.delete(doc(collection(db, 'lessons'), lId))
    }
  }
  await batch.commit()
}

export const dbDuplicateCourse = async (
  c: ICourse,
  lessons: Record<string, ILesson>
) => {
  const batches = []
  let batch = writeBatch(db)
  batches.push(batch)
  let counter = 0

  const getBatch = () => {
    counter += 1
    if (counter >= 200) {
      batch = writeBatch(db)
      counter = 0
      batches.push(batch)
      console.log('new batch created')
    }
    return batch
  }
  const courseId = generateId()
  const newCourse: ICourse = {
    ...c,
    id: courseId,
    lessons: [],
    closed: true,
    invisible: true,
    createdAt: +dayjs(),
    title: c.title + ' (copy)',
    copyOf: {
      entityId: c.id,
      entityType: IEntityType.COURSES
    }
  }
  if (c.lessons) {
    for (const lId of c.lessons) {
      const l = lessons[lId]
      if (l) {
        const newL: ILesson = {
          ...l,
          id: generateId(),
          entityId: courseId
        }
        newCourse.lessons.push(newL.id)
        const b = getBatch()
        b.set(doc(collection(db, 'lessons'), newL.id), newL)
      }
    }
  }
  if (c.content) {
    const newContent: IWeek[] = []
    forEach(c.content, (w) => {
      const newWeek: IWeek = { ...w, days: [], enabled: false }
      forEach(w.days, (d) => {
        const newDay: IDay = { ...d, lessons: [] }
        forEach(d.lessons, (lId) => {
          const l = lessons[lId]
          if (l) {
            const newL: ILesson = {
              ...l,
              id: generateId(),
              entityId: courseId
            }
            newDay.lessons.push(newL.id)
            // console.log('add new lesson to batch', newL)
            const b = getBatch()
            b.set(doc(collection(db, 'lessons'), newL.id), newL)
          }
        })
        newWeek.days.push(newDay)
      })
      newContent.push(newWeek)
    })
    newCourse.content = newContent
  }

  console.log('course diplication', batches.length)

  const b = getBatch()
  b.set(doc(collection(db, 'courses'), courseId), newCourse)
  for (const b of batches) {
    await b.commit()
    console.log('batch commited')
  }
  return courseId
}

export const dbDuplicateCourseFree = async (
  c: ICourse,
  lessons: Record<string, ILesson>
) => {
  const batch = writeBatch(db)
  const courseId = generateId()
  const newCourse: IFree = {
    ...c,
    id: courseId,
    lessons: [],
    closed: true,
    invisible: true,
    createdAt: +dayjs(),
    copyOf: {
      entityId: c.id,
      entityType: IEntityType.COURSES
    }
  }
  if (c.lessons) {
    for (const lId of c.lessons) {
      const l = lessons[lId]
      if (l) {
        const newL: ILesson = {
          ...l,
          id: generateId(),
          entityId: courseId
        }
        newCourse.lessons.push(newL.id)
        batch.set(doc(collection(db, 'lessons'), newL.id), newL)
      }
    }
  }
  if (c.content) {
    const newContent: IWeek[] = []
    forEach(c.content, (w) => {
      const newWeek: IWeek = { ...w, days: [], enabled: false }
      forEach(w.days, (d) => {
        const newDay: IDay = { ...d, lessons: [] }
        forEach(d.lessons, (lId) => {
          const l = lessons[lId]
          if (l) {
            const newL: ILesson = {
              ...l,
              id: generateId(),
              entityId: courseId
            }
            newDay.lessons.push(newL.id)
            // console.log('add new lesson to batch', newL)
            batch.set(doc(collection(db, 'lessons'), newL.id), newL)
          }
        })
        newWeek.days.push(newDay)
      })
      newContent.push(newWeek)
    })
    newCourse.content = newContent
  }

  // console.log('new course', newCourse)

  batch.set(doc(collection(db, 'free'), courseId), newCourse)
  await batch.commit()
  return courseId
}

export const dbDuplicateCourseLibrary = async (
  c: ICourse,
  lessons: Record<string, ILesson>
) => {
  const batch = writeBatch(db)
  const courseId = generateId()
  const newCourse: ICourse = {
    ...c,
    id: courseId,
    lessons: [],
    closed: true,
    invisible: true,
    createdAt: +dayjs(),
    copyOf: {
      entityId: c.id,
      entityType: IEntityType.COURSES
    }
  }
  if (c.lessons) {
    for (const lId of c.lessons) {
      const l = lessons[lId]
      if (l) {
        const newL: ILesson = {
          ...l,
          id: generateId(),
          entityId: courseId
        }
        newCourse.lessons.push(newL.id)
        batch.set(doc(collection(db, 'lessons'), newL.id), newL)
      }
    }
  }
  if (c.content) {
    const newContent: IWeek[] = []
    forEach(c.content, (w) => {
      const newWeek: IWeek = { ...w, days: [], enabled: false }
      forEach(w.days, (d) => {
        const newDay: IDay = { ...d, lessons: [] }
        forEach(d.lessons, (lId) => {
          const l = lessons[lId]
          if (l) {
            const newL: ILesson = {
              ...l,
              id: generateId(),
              entityId: courseId
            }
            newDay.lessons.push(newL.id)
            // console.log('add new lesson to batch', newL)
            batch.set(doc(collection(db, 'lessons'), newL.id), newL)
          }
        })
        newWeek.days.push(newDay)
      })
      newContent.push(newWeek)
    })
    newCourse.content = newContent
  }

  // console.log('new course', newCourse)

  batch.set(doc(collection(db, 'library'), courseId), newCourse)
  await batch.commit()
  return courseId
}
